import { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@components/ui/dialog";
import { toast } from "sonner";
import { useInviteMember } from "~/api/workspaces.hooks";
import { useCurrentAccount } from "~/stores/sessionStore";

interface InviteMembersModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InviteMembersModal({ open, onOpenChange }: InviteMembersModalProps) {
  const [email, setEmail] = useState("");
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState<number[]>([]);
  const submittingRef = useRef(false);
  const currentAccount = useCurrentAccount();
  const inviteMember = useInviteMember();

  const workspaces = currentAccount?.workspaces ?? [];

  // Select all workspaces when modal opens
  useEffect(() => {
    if (open && workspaces.length > 0) {
      setSelectedWorkspaceIds(workspaces.map((ws) => ws.id));
    }
  }, [open, workspaces.length]);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setEmail("");
      setSelectedWorkspaceIds([]);
    }
    onOpenChange(isOpen);
  };

  const toggleWorkspace = (id: number) => {
    setSelectedWorkspaceIds((prev) =>
      prev.includes(id) ? prev.filter((wid) => wid !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || selectedWorkspaceIds.length === 0 || submittingRef.current) return;
    submittingRef.current = true;

    try {
      await inviteMember.mutateAsync({
        email: email.trim(),
        workspaceIds: selectedWorkspaceIds,
      });
      toast.success("Invitation sent", {
        description: `Invited ${email.trim()} to ${selectedWorkspaceIds.length} workspace${selectedWorkspaceIds.length > 1 ? "s" : ""}`,
      });
      setEmail("");
      setSelectedWorkspaceIds([]);
      onOpenChange(false);
    } catch (error) {
      toast.error("Failed to invite member", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      submittingRef.current = false;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="invite-members-modal">
        <DialogHeader>
          <DialogTitle>Invite members</DialogTitle>
          <DialogDescription>
            {workspaces.length > 1
              ? "Invite someone by email. Select which workspaces they should have access to."
              : "Invite someone by email to join your workspace."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3 mt-2">
          <input
            type="email"
            data-testid="invite-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@example.com"
            className="w-full text-sm px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            disabled={inviteMember.isPending}
            autoFocus
          />

          {workspaces.length > 1 && (
            <div className="flex flex-col gap-1.5" data-testid="workspace-checkboxes">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-muted-foreground">Workspaces</label>
                <button
                  type="button"
                  data-testid="workspace-checkbox-toggle-all"
                  onClick={() => {
                    if (selectedWorkspaceIds.length === workspaces.length) {
                      setSelectedWorkspaceIds([]);
                    } else {
                      setSelectedWorkspaceIds(workspaces.map((ws) => ws.id));
                    }
                  }}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  disabled={inviteMember.isPending}
                >
                  {selectedWorkspaceIds.length === workspaces.length ? "Deselect all" : "Select all"}
                </button>
              </div>
              {workspaces.map((ws) => (
                <label
                  key={ws.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer text-sm"
                  data-testid={`workspace-checkbox-${ws.id}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedWorkspaceIds.includes(ws.id)}
                    onChange={() => toggleWorkspace(ws.id)}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                    disabled={inviteMember.isPending}
                  />
                  <span>{ws.name}</span>
                </label>
              ))}
            </div>
          )}

          <button
            type="submit"
            data-testid="invite-send-button"
            disabled={!email.trim() || selectedWorkspaceIds.length === 0 || inviteMember.isPending}
            className="w-full px-3 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {inviteMember.isPending ? "Sending..." : "Send invite"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
